import {
  type CollectSessionOpenedResponse,
  type CollectSessionResponse,
  type CollectSignalResponse,
  SemanticType,
  SessionStatus,
} from '@/api/generated'
import { collectApi } from '@/api/collect'
import { collectEmbeddedData, firstQueryString } from '@/utils/collect-embedded-data'
import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { displayWording, referencedAnswerIds } from '@/utils/wording-template'
import { getErrorMessage } from '@/api/helpers'
import { posthogCapture, registerCollectionContext } from '@/plugins/posthog'
import { useVisibleQuestions } from '@/composables/useVisibleQuestions'
import type { LocationQuery } from 'vue-router'

/** Stored answer for one question. `null` clears an existing answer on PATCH. */
export type AnswerValue = string | number | boolean | string[] | null

export type CollectPhase =
  'idle' | 'loading' | 'ready' | 'opening' | 'in_progress' | 'finalizing' | 'completed' | 'error'

type StoredSession = {
  sessionId: string
  accessToken: string
}

function storageKey(linkToken: string) {
  return `collect:session:${linkToken}`
}

function readStoredSession(linkToken: string): StoredSession | null {
  try {
    const raw = localStorage.getItem(storageKey(linkToken))

    if (!raw) return null

    const parsed = JSON.parse(raw) as Partial<StoredSession>

    if (typeof parsed.sessionId !== 'string' || typeof parsed.accessToken !== 'string') return null

    return { sessionId: parsed.sessionId, accessToken: parsed.accessToken }
  } catch {
    return null
  }
}

function writeStoredSession(linkToken: string, session: StoredSession) {
  localStorage.setItem(storageKey(linkToken), JSON.stringify(session))
}

function clearStoredSession(linkToken: string) {
  localStorage.removeItem(storageKey(linkToken))
}

function analyticsContextFromRuntime(
  runtime: CollectSignalResponse,
  extras: { linkToken?: string | null; sessionId?: string | null } = {}
) {
  return {
    organization_id: runtime.organization_id,
    project_id: runtime.project_id,
    signal_id: runtime.signal_id,
    is_preview: runtime.is_preview,
    link_token: extras.linkToken ?? null,
    session_id: extras.sessionId ?? null,
  }
}

export const useCollectStore = defineStore('collect', () => {
  const phase = ref<CollectPhase>('idle')
  const linkToken = ref<string | null>(null)
  const runtime = ref<CollectSignalResponse | null>(null)
  const sessionId = ref<string | null>(null)
  const accessToken = ref<string | null>(null)
  const sessionStatus = ref<SessionStatus | null>(null)
  const answers = ref<Record<string, AnswerValue>>({})
  /** Server-rendered wording strings keyed by question or group id. */
  const wordings = ref<Record<string, string>>({})
  /** Latest `hidden_question_ids` from open, resume, answers PATCH, or probe. */
  const hiddenQuestionIds = ref<string[]>([])
  /** Answer ids committed locally whose PATCH/probe response has not been applied yet. */
  const pendingWordingIds = ref<Set<string>>(new Set())
  /** Monotonic token so an older overlapping response cannot overwrite newer wordings. */
  let wordingsTokenSeq = 0
  let lastAppliedWordingsToken = 0
  const errorMessage = ref<string | null>(null)

  const questions = computed(() => runtime.value?.questions ?? [])
  const { visibleQuestions } = useVisibleQuestions(questions, hiddenQuestionIds, pendingWordingIds)
  // `message` has no storage/answer — show the wording bubble, never wait for a response.
  const answerableQuestions = computed(() =>
    visibleQuestions.value.filter((question) => question.semantic_type !== SemanticType.MESSAGE)
  )

  /** Runtime wording templates keyed by question or group id. */
  const wordingTemplateById = computed(() => {
    const map = new Map<string, string>()

    for (const question of runtime.value?.questions ?? []) map.set(question.id, question.wording)
    for (const group of runtime.value?.question_groups ?? []) map.set(group.id, group.wording)

    return map
  })

  /** Reveal deps: question/group id → answer ids its wording template or show_if reads. */
  const wordingDepsById = computed(() => {
    const map = new Map<string, Set<string>>()

    for (const [id, template] of wordingTemplateById.value) map.set(id, referencedAnswerIds(template))

    for (const question of runtime.value?.questions ?? []) {
      for (const depId of question.show_if_question_ids) map.get(question.id)?.add(depId)
    }

    return map
  })

  function setAnswer(questionId: string, value: AnswerValue) {
    answers.value = { ...answers.value, [questionId]: value }
  }

  function nextWordingsToken() {
    wordingsTokenSeq += 1

    return wordingsTokenSeq
  }

  /** Wordings and hidden question ids from an open/resume/PATCH/probe/finalize response. */
  function applyServerState(
    response: Pick<CollectSessionResponse, 'wordings' | 'hidden_question_ids'>,
    token?: number
  ) {
    if (typeof token === 'number') {
      // Reject stale in-flight responses (including ones that raced a session apply).
      if (token <= lastAppliedWordingsToken) return

      lastAppliedWordingsToken = token
    } else {
      // Session open/resume/finalize always apply and invalidate older request tokens.
      lastAppliedWordingsToken = wordingsTokenSeq
    }

    wordings.value = { ...wordings.value, ...response.wordings }
    hiddenQuestionIds.value = response.hidden_question_ids
  }

  function markWordingPending(questionId: string) {
    const next = new Set(pendingWordingIds.value)

    next.add(questionId)
    pendingWordingIds.value = next
  }

  function clearWordingPending(ids: Iterable<string>) {
    const next = new Set(pendingWordingIds.value)
    let changed = false

    for (const id of ids) {
      if (next.delete(id)) changed = true
    }

    if (changed) pendingWordingIds.value = next
  }

  /** Server-rendered wording for a question or group id; never exposes raw `{{ … }}` templates. */
  function wordingFor(id: string): string {
    return displayWording(id, wordingTemplateById.value.get(id) ?? '', wordings.value)
  }

  /** True while this wording or show_if still depends on an answer whose PATCH/probe has not landed. */
  function isWordingHeld(id: string): boolean {
    const deps = wordingDepsById.value.get(id)

    if (!deps || deps.size === 0) return false

    for (const depId of deps) {
      if (pendingWordingIds.value.has(depId)) return true
    }

    return false
  }

  function applySession(session: CollectSessionResponse | CollectSessionOpenedResponse) {
    sessionId.value = session.id
    sessionStatus.value = session.status
    answers.value = { ...(session.answers as Record<string, AnswerValue>) }
    applyServerState(session)
    pendingWordingIds.value = new Set()

    if ('access_token' in session && typeof session.access_token === 'string') {
      accessToken.value = session.access_token
    }
  }

  function persistSession() {
    if (!linkToken.value || !sessionId.value || !accessToken.value) return

    writeStoredSession(linkToken.value, {
      sessionId: sessionId.value,
      accessToken: accessToken.value,
    })
  }

  async function tryResumeStoredSession(): Promise<boolean> {
    if (!linkToken.value) return false

    const stored = readStoredSession(linkToken.value)

    if (!stored) return false

    try {
      const session = await collectApi.getSessionDetail(stored.sessionId, stored.accessToken)

      if (session.status === SessionStatus.COMPLETED || session.status === SessionStatus.PARTIAL) {
        clearStoredSession(linkToken.value)
        applySession(session)
        phase.value = session.status === SessionStatus.COMPLETED ? 'completed' : 'ready'

        return false
      }

      // Session is pinned at open; a republished runtime must not reuse it.
      if (runtime.value && session.signal_version_id !== runtime.value.signal_version_id) {
        clearStoredSession(linkToken.value)

        return false
      }

      accessToken.value = stored.accessToken
      applySession(session)
      phase.value = 'in_progress'

      return true
    } catch {
      clearStoredSession(linkToken.value)

      return false
    }
  }

  async function bootstrap(token: string, options: { resumeToken?: string | null; query?: LocationQuery } = {}) {
    linkToken.value = token
    phase.value = 'loading'
    errorMessage.value = null
    runtime.value = null
    sessionId.value = null
    accessToken.value = null
    sessionStatus.value = null
    answers.value = {}
    wordings.value = {}
    hiddenQuestionIds.value = []
    pendingWordingIds.value = new Set()
    wordingsTokenSeq = 0
    lastAppliedWordingsToken = 0

    try {
      runtime.value = await collectApi.getSignalRuntime(token)

      registerCollectionContext(
        analyticsContextFromRuntime(runtime.value, {
          linkToken: token,
          sessionId: sessionId.value,
        })
      )
      posthogCapture('collection_runtime_loaded', {
        signal_id: runtime.value.signal_id,
        is_preview: runtime.value.is_preview,
      })

      // Authoring opens preview with ?fresh=1 so we mint a new test session instead of resuming.
      const forceFreshPreview = runtime.value.is_preview && firstQueryString(options.query?.fresh) === '1'

      const resumed = forceFreshPreview ? false : await tryResumeStoredSession()

      if (resumed && runtime.value) {
        registerCollectionContext(
          analyticsContextFromRuntime(runtime.value, {
            linkToken: token,
            sessionId: sessionId.value,
          })
        )
        posthogCapture('collection_session_resumed', { session_id: sessionId.value })
      }

      if (!resumed) {
        // Skip welcome CTAs into typing mode when voice is off, or for authoring preview
        // (mic still needs a user gesture — preview never auto-starts live voice).
        const skipWelcome = !runtime.value.voice_mode_enabled || runtime.value.is_preview

        if (skipWelcome) {
          await startSession({
            resumeToken: options.resumeToken,
            query: options.query,
          })
        } else {
          phase.value = 'ready'
        }
      }
    } catch (error) {
      phase.value = 'error'
      errorMessage.value = getErrorMessage(error, 'Could not load this questionnaire.')
    }
  }

  async function startSession(options: { resumeToken?: string | null; query?: LocationQuery } = {}) {
    if (!linkToken.value || !runtime.value) return
    if (sessionId.value && phase.value === 'in_progress') return

    phase.value = 'opening'
    errorMessage.value = null

    try {
      const embeddedData = collectEmbeddedData(options.query ?? {}, runtime.value.embedded_data_keys)
      const opened = await collectApi.openSession(linkToken.value, {
        resume_token: options.resumeToken ?? null,
        embedded_data: embeddedData,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
      })

      applySession(opened)
      persistSession()
      phase.value = 'in_progress'

      if (runtime.value) {
        registerCollectionContext(
          analyticsContextFromRuntime(runtime.value, {
            linkToken: linkToken.value,
            sessionId: sessionId.value,
          })
        )
      }

      posthogCapture('collection_session_started', {
        session_id: sessionId.value,
        is_test: opened.is_test,
        embedded_data_keys: embeddedData ? Object.keys(embeddedData) : [],
      })
    } catch (error) {
      phase.value = 'error'
      errorMessage.value = getErrorMessage(error, 'Could not start the session.')
    }
  }

  async function finalizeSession() {
    if (!sessionId.value || !accessToken.value) return
    // Guard concurrent callers (commit + resume watcher) so finalize is POSTed once.
    if (phase.value === 'finalizing' || phase.value === 'completed') return

    phase.value = 'finalizing'

    try {
      const session = await collectApi.finalize(sessionId.value, accessToken.value)
      const localAnswers = { ...answers.value }

      applySession(session)
      // Finalize payload can omit the last PATCH; keep optimistic answers so bubbles stay filled.
      answers.value = { ...answers.value, ...localAnswers }
      if (linkToken.value) clearStoredSession(linkToken.value)
      phase.value = 'completed'

      posthogCapture('collection_session_completed', { session_id: sessionId.value })
    } catch (error) {
      phase.value = 'error'
      errorMessage.value = getErrorMessage(error, 'Could not submit your answers.')
    }
  }

  async function sealPartialSession() {
    if (!sessionId.value || !accessToken.value) return

    try {
      const session = await collectApi.sealPartial(sessionId.value, accessToken.value)

      applySession(session)
      if (linkToken.value) clearStoredSession(linkToken.value)

      posthogCapture('collection_session_sealed_partial', { session_id: sessionId.value })
    } catch (error) {
      errorMessage.value = getErrorMessage(error, 'Could not save your progress.')
    }
  }

  return {
    phase,
    linkToken,
    runtime,
    sessionId,
    accessToken,
    sessionStatus,
    answers,
    wordings,
    hiddenQuestionIds,
    pendingWordingIds,
    wordingDepsById,
    errorMessage,
    questions,
    visibleQuestions,
    answerableQuestions,
    setAnswer,
    nextWordingsToken,
    applyServerState,
    markWordingPending,
    clearWordingPending,
    wordingFor,
    isWordingHeld,
    bootstrap,
    startSession,
    finalizeSession,
    sealPartialSession,
  }
})
